import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { map, Observable, ReplaySubject, shareReplay, tap } from 'rxjs';
import { AuthService, ROL_ADMIN, ROL_AUDITOR, ROL_SUPERVISOR, User, usuarioTieneAccesoPorPermiso, usuarioTienePrivilegio } from '../auth/auth.service';
import { ACCION, MODULO } from '../permisos/permiso.constants';

/** Requisito de privilegio para mostrar un ítem de navegación (gating de UX, no de seguridad). */
type RequierePrivilegio = { moduloClave: string; accionClave: string };

const funcionarioRoutes: FuseNavigationItem[] = [
  {
    id: 'dashboards',
    title: '',
    subtitle: '',
    type: 'group',
    icon: 'heroicons_outline:home',
    children: [
      {
        id: 'dashboards.project',
        title: 'Módulo',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/funcionariogeneral',
      },
    ],
  },
];

const supervisorRoutes: FuseNavigationItem[] = [
  {
    id: 'catalogos',
    title: 'Catálogos',
    type: 'group',
    icon: 'heroicons_outline:document-duplicate',
    children: [
      {
        id: 'grupos',
        title: 'Grupos',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablagrupo',
        meta: { requiere: { moduloClave: MODULO.GRUPOS, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'perfiles',
        title: 'Perfiles',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablaperfil',
        meta: { requiere: { moduloClave: MODULO.PERFILES_SEGURIDAD, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'modulos',
        title: 'Módulos',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablamodulo',
        meta: { requiere: { moduloClave: MODULO.PUESTOS, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'salas',
        title: 'Salas',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablasala',
        meta: { requiere: { moduloClave: MODULO.SALAS, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'kioskos',
        title: 'Kioskos',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablakiosko',
        meta: { requiere: { moduloClave: MODULO.KIOSKOS, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'multimedia',
        title: 'Multimedia',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablamultimedia',
        meta: { requiere: { moduloClave: MODULO.MULTIMEDIA, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'avisos',
        title: 'Avisos',
        type: 'basic',
        icon: 'heroicons_outline:megaphone',
        link: '/tablaavisos',
        meta: { requiere: { moduloClave: MODULO.AVISOS, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'bloqueos.telefonos',
        title: 'Teléfonos bloqueados',
        type: 'basic',
        icon: 'heroicons_outline:no-symbol',
        link: '/tablatelefonosbloqueados',
        meta: { requiere: { moduloClave: MODULO.TELEFONOS_BLOQUEADOS, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'bloqueos.ips',
        title: 'IPs bloqueadas',
        type: 'basic',
        icon: 'heroicons_outline:shield-exclamation',
        link: '/tablaipsbloqueadas',
        meta: { requiere: { moduloClave: MODULO.IPS_BLOQUEADAS, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'tramites',
        title: 'Trámites',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablatramites',
        meta: { requiere: { moduloClave: MODULO.TRAMITES, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'limites-canal',
        title: 'Límites por canal',
        type: 'basic',
        icon: 'heroicons_outline:adjustments-horizontal',
        link: '/tablalimitescanal',
        meta: { requiere: { moduloClave: MODULO.LIMITES_CANAL, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'citas',
        title: 'Citas',
        type: 'basic',
        icon: 'heroicons_outline:calendar-days',
        link: '/tablacitas',
        meta: { requiere: { moduloClave: MODULO.CITAS, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'usuarios',
        title: 'Usuarios',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablausuario',
        meta: { requiere: { moduloClave: MODULO.USUARIOS, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
    ]
  },
];

const adminRoutes: FuseNavigationItem[] = [
  {
    id: 'admin',
    title: 'Admin',
    type: 'group',
    icon: 'heroicons_outline:document-duplicate',
    children: [
      {
        id: 'sucursales',
        title: 'Sucursales',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablasucursal',
        meta: { requiere: { moduloClave: MODULO.SUCURSALES, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'permisos',
        title: 'Permisos',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/tablapermiso',
        meta: { requiere: { moduloClave: MODULO.PERMISOS, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'configuracion-aplicacion',
        title: 'Configuración',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/configuracionaplicacion',
        meta: { requiere: { moduloClave: MODULO.CONFIG_APLICACION, accionClave: ACCION.EDITAR } } satisfies { requiere: RequierePrivilegio },
      }
    ]
  },
];

const reporteRoutes: FuseNavigationItem[] = [
  {
    id: 'reportes',
    title: 'Reportes',
    type: 'group',
    icon: 'heroicons_outline:document-duplicate',
    children: [
      {
        id: 'turnos',
        title: 'Turnos',
        type: 'basic',
        icon: 'heroicons_outline:check-circle',
        link: '/reporte-turnos',
        meta: { requiere: { moduloClave: MODULO.REPORTES, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'turnos-atendidos',
        title: 'Turnos Atendidos',
        type: 'basic',
        icon: 'heroicons_outline:user-group',
        link: '/reporte-turnos-atendidos',
        meta: { requiere: { moduloClave: MODULO.REPORTES, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'comparativo-turnos',
        title: 'Comparativo de Emisión',
        type: 'basic',
        icon: 'heroicons_outline:chart-bar',
        link: '/reporte-comparativo-turnos',
        meta: { requiere: { moduloClave: MODULO.REPORTES, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'reporte-citas',
        title: 'Citas',
        type: 'basic',
        icon: 'heroicons_outline:calendar',
        link: '/reporte-citas',
        meta: { requiere: { moduloClave: MODULO.REPORTES, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'bitacora-turnos',
        title: 'Bitácora de Turnos',
        type: 'basic',
        icon: 'heroicons_outline:clipboard-document-list',
        link: '/bitacoraturnos',
        meta: { requiere: { moduloClave: MODULO.BITACORA_TURNO, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      },
      {
        id: 'registro-chatbot',
        title: 'Registro de Chatbot',
        type: 'basic',
        icon: 'heroicons_outline:chat-bubble-left-right',
        link: '/registro-chatbot',
        meta: { requiere: { moduloClave: MODULO.REGISTRO_CHATBOT, accionClave: ACCION.CONSULTAR } } satisfies { requiere: RequierePrivilegio },
      }
    ]
  },
];

/**
 * Filtra los hijos de un grupo de navegación combinando Rol y Permiso como dos vías
 * independientes hacia el mismo resultado:
 * - Si `rolGate` es true (el Rol ya da acceso a este grupo), el ítem se muestra salvo que el
 *   usuario tenga Permisos asignados que específicamente lo excluyan (política existente: sin
 *   Permiso asignado no restringe nada; con Permiso asignado, solo se ve lo que ese Permiso otorga).
 * - Si `rolGate` es false (el Rol no alcanza para este grupo), el ítem solo se muestra si un
 *   Permiso activo otorga explícitamente ese privilegio — el Permiso desbloquea acceso
 *   independiente del Rol.
 * Un grupo cuyos hijos quedan todos ocultos se omite para no dejar secciones vacías.
 */
function filtrarGrupo(grupo: FuseNavigationItem, rolGate: boolean, user: User): FuseNavigationItem | null {
  if (!grupo.children) {
    return rolGate ? grupo : null;
  }

  const children = grupo.children.filter(item => {
    const requiere = (item.meta as { requiere?: RequierePrivilegio })?.requiere;
    if (rolGate) {
      return !requiere || usuarioTienePrivilegio(user, requiere.moduloClave, requiere.accionClave);
    }

    return !!requiere && usuarioTieneAccesoPorPermiso(user, requiere.moduloClave, requiere.accionClave);
  });

  return children.length > 0 ? { ...grupo, children } : null;
}

/** Recorre la navegación ya filtrada y devuelve el primer link navegable (profundidad primero). */
function primerLink(items: FuseNavigationItem[]): string | null {
  for (const item of items) {
    if (item.link) {
      return item.link;
    }

    if (item.children) {
      const link = primerLink(item.children);
      if (link) {
        return link;
      }
    }
  }

  return null;
}

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private authService = inject(AuthService)
  private _httpClient = inject(HttpClient);
  private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for navigation
   */
  get navigation$(): Observable<Navigation> {
    return this.authService.getUser().pipe(
      map(user => this.construirRoutes(user)),
      map(routes => ({
        compact: routes,
        default: routes,
        futuristic: routes,
        horizontal: routes,
      })),
      shareReplay(1)
    );
  }

  /**
   * Primer link navegable de la navegación calculada para el usuario actual, o null si no tiene
   * ninguno (ni por Rol ni por Permiso) — se usa para redirigir después del login en vez del
   * destino fijo `/funcionariogeneral`.
   */
  getPrimerLinkDisponible(): Observable<string | null> {
    return this.authService.getUser().pipe(
      map(user => primerLink(this.construirRoutes(user)))
    );
  }

  private construirRoutes(user: User): FuseNavigationItem[] {
    const routes: FuseNavigationItem[] = [];
    if (user.requiereCambioPass) {
      return routes;
    }

    if (user.puestoAsignado) {
      routes.push(...funcionarioRoutes);
    }

    let isAdmin = false;
    let isSupervisor = false;
    let isAuditor = false;
    for (const rol of user.roles) {
      if (rol.clave === ROL_ADMIN) {
        isAdmin = true;
        break;
      }

      if (rol.clave === ROL_SUPERVISOR) {
        isSupervisor = true;
      }

      if (rol.clave === ROL_AUDITOR) {
        isAuditor = true;
      }
    }

    const gruposCandidatos: { grupo: FuseNavigationItem; rolGate: boolean }[] = [
      ...supervisorRoutes.map(grupo => ({ grupo, rolGate: isAdmin || isSupervisor })),
      ...adminRoutes.map(grupo => ({ grupo, rolGate: isAdmin })),
      ...reporteRoutes.map(grupo => ({ grupo, rolGate: isAdmin || isAuditor })),
    ];

    for (const { grupo, rolGate } of gruposCandidatos) {
      const filtrado = filtrarGrupo(grupo, rolGate, user);
      if (filtrado) {
        routes.push(filtrado);
      }
    }

    return routes;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all navigation data
   */
  get(): Observable<Navigation> {
    return this._httpClient.get<Navigation>('api/common/navigation').pipe(
      tap((navigation) => {
        this._navigation.next(navigation);
      }),
    );
  }
}
